import {
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import {
    filter,
    finalize,
    fromEvent,
    merge,
    Subscription,
    switchMap,
    takeUntil,
    tap,
} from "rxjs";

import {
    distance,
    getTouchPair,
    isOneFingerTouch,
    isTwoFingersTouch,
    midpoint,
} from "../gestures.helper";
import { PinchEvent, Position } from "../gestures.types";

/**
 * @description
 *
 * A directive to track pinch events on element (allowing zooming via pinch gestures).
 *
 * @usageNotes
 *
 * Set pinch events on an HTML element and control the zoom limits using `minScale` and `maxScale`.
 *
 * - `minScale`: The minimum scale factor allowed during a pinch gesture. Prevents zooming out beyond this value.
 * - `maxScale`: The maximum scale factor allowed during a pinch gesture. Prevents zooming in beyond this value.
 *
 * ```
 * <some-element hPinch [minScale]="0.5" [maxScale]="2" (pinchStart)="onStartPinch()" (pinchMove)="onPinchMove()" (pinchEnd)="onPinchEnd()">
 *  ...
 * </some-element>
 * ```
 */
@Directive({
    selector: "[hPinch]",
    standalone: true,
    host: {
        "[style.touch-action]": '"none"',
    },
})
export class PinchDirective implements OnInit, OnDestroy {
    @Input() public initialScale = 1;

    @Input() public maxScale = Infinity;
    @Input() public minScale = -Infinity;

    @Output() public pinchStart = new EventEmitter<PinchEvent>();
    @Output() public pinchMove = new EventEmitter<PinchEvent>();
    @Output() public pinchEnd = new EventEmitter<void>();

    private subscription = new Subscription();
    private initialDistance?: number;
    private initialMidpoint?: Position;
    private lastScale?: number;

    constructor(private element: ElementRef<HTMLElement>) {}

    public ngOnInit(): void {
        const element = this.element.nativeElement;

        // The pinch gesture starts with two fingers on the screen.
        const start$ = fromEvent<TouchEvent>(element, "touchstart").pipe(
            filter(isTwoFingersTouch),
            tap((event) => event.preventDefault()),
        );
        // The pinch gesture continues as long as the user keep two fingers on screen.
        const move$ = fromEvent<TouchEvent>(element, "touchmove", {
            passive: false,
        }).pipe(
            filter(isTwoFingersTouch),
            tap((event) => event.preventDefault()),
        );
        // The pinch gesture ends if one finger leaves the screen.
        const end$ = merge(
            fromEvent<TouchEvent>(element, "touchend"),
            start$.pipe(filter(isOneFingerTouch)),
        );

        const pinch$ = start$.pipe(
            tap((event) => {
                const [firstTouch, secondTouch] = getTouchPair(event);
                this.initialDistance = distance(firstTouch, secondTouch);
                this.initialMidpoint = midpoint(firstTouch, secondTouch);
                this.pinchStart.emit({
                    focalPoint: this.initialMidpoint,
                    scale: this.initialScale,
                });
            }),
            switchMap(() =>
                move$.pipe(
                    tap((event) => {
                        const [firstTouch, secondTouch] = getTouchPair(event);
                        if (this.initialDistance) {
                            // We calculate new scale based on distance change.
                            const currentDistance = distance(
                                firstTouch,
                                secondTouch,
                            );
                            const currentMid = midpoint(
                                firstTouch,
                                secondTouch,
                            );
                            const rawScale =
                                this.initialDistance > 0
                                    ? this.initialScale *
                                      (currentDistance / this.initialDistance)
                                    : 1;
                            this.lastScale = Math.max(
                                this.minScale,
                                Math.min(rawScale, this.maxScale),
                            );

                            this.pinchMove.emit({
                                scale: this.lastScale,
                                focalPoint: currentMid,
                            });
                        }
                    }),
                    takeUntil(end$),
                    finalize(() => {
                        // We keep last scale calculated in memory to resume a pinch.
                        if (this.lastScale) {
                            this.initialScale = this.lastScale;
                        }
                        this.pinchEnd.emit();
                    }),
                ),
            ),
        );

        this.subscription = pinch$.subscribe();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
